import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Star, Lock, Edit2, Check, X } from 'lucide-react';
import ErrorModal from '@/components/ErrorModal';

const FLAG_URL = (code) => `https://flagcdn.com/w80/${code?.toLowerCase()}.png`;

// Función para calcular puntos
const calculatePoints = (prediction, result) => {
  if (!prediction || !result) return 0;
  
  const { home_goals: predHome, away_goals: predAway } = prediction;
  const { home_goals: resHome, away_goals: resAway } = result;
  
  // 3 puntos por marcador exacto
  if (predHome === resHome && predAway === resAway) {
    return 3;
  }
  
  // 1 punto por resultado correcto (ganador o empate)
  const predResult = predHome > predAway ? 'home' : predHome < predAway ? 'away' : 'draw';
  const actualResult = resHome > resAway ? 'home' : resHome < resAway ? 'away' : 'draw';
  
  if (predResult === actualResult) {
    return 1;
  }
  
  return 0;
};

export default function Predictions() {
  const [matches, setMatches] = useState([]);
  const [results, setResults] = useState({});
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState('all');
  const [filterType, setFilterType] = useState('pendientes'); // 'pendientes' o 'historial'
  const [predictions, setPredictions] = useState({});
  const [savedPredictions, setSavedPredictions] = useState({}); // Predicciones guardadas
  const [editingMatch, setEditingMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMatch, setSavingMatch] = useState(null);

  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [allMatches, userPredictions, s, r] = await Promise.all([
        api.get('/matches/enriched'), // Obtener TODOS los partidos
        api.get('/predictions'), // Obtener predicciones del usuario
        api.get('/stages'),
        api.get('/results')
      ]);
      
      // Mapear predicciones del usuario por match_id
      const userPredictionsMap = {};
      userPredictions.data.forEach(pred => {
        userPredictionsMap[pred.match_id] = {
          home_goals: pred.home_goals,
          away_goals: pred.away_goals
        };
      });
      
      // Agregar predicciones a los partidos y calcular is_locked
      const enrichedMatches = allMatches.data.map(match => {
        const prediction = userPredictionsMap[match.id];
        
        // Calcular is_locked (mismo lógica que en backend)
        const matchDateTime = new Date(`${match.date}T${match.time}:00Z`);
        const gmtMinus6Time = new Date(matchDateTime.getTime() + (6 * 60 * 60 * 1000));
        const oneMinuteBefore = new Date(gmtMinus6Time.getTime() - (1 * 60 * 1000));
        const now = new Date();
        const is_locked = now >= oneMinuteBefore;
        
        return {
          ...match,
          prediction,
          is_locked
        };
      });
      
      setMatches(enrichedMatches);
      setStages(s.data);
      
      // Mapear resultados
      const resultsMap = {};
      r.data.forEach(res => {
        resultsMap[res.match_id] = { home_goals: res.home_goals, away_goals: res.away_goals };
      });
      setResults(resultsMap);
      
      // Cargar predicciones guardadas
      const initial = {};
      const saved = {};
      enrichedMatches.forEach(match => {
        if (match.prediction) {
          initial[match.id] = { home_goals: match.prediction.home_goals, away_goals: match.prediction.away_goals };
          saved[match.id] = true;
        }
      });
      setPredictions(initial);
      setSavedPredictions(saved);
    } finally {
      setLoading(false);
    }
  };

  const updatePrediction = (matchId, field, value) => {
    if (value === '' || value === '0') {
      setPredictions(prev => ({
        ...prev,
        [matchId]: { ...prev[matchId], [field]: 0 }
      }));
      return;
    }
    
    const num = parseInt(value);
    if (isNaN(num) || num < 0) return;
    setPredictions(prev => ({
      ...prev,
      [matchId]: { ...prev[matchId], [field]: num }
    }));
  };

  const handleSaveIndividual = async (matchId) => {
    const pred = predictions[matchId];
    if (!pred || pred.home_goals === undefined || pred.away_goals === undefined) {
      setErrorModal({
        open: true,
        title: 'Predicción incompleta',
        message: 'Por favor, ingresa ambos marcadores antes de guardar.'
      });
      return;
    }

    setSavingMatch(matchId);
    try {
      await api.post('/predictions', [{
        match_id: matchId,
        home_goals: pred.home_goals,
        away_goals: pred.away_goals,
      }]);
      toast.success('Predicción guardada');
      setSavedPredictions(prev => ({ ...prev, [matchId]: true }));
      setEditingMatch(null);
      await loadData(); // Recargar para actualizar estados
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error al guardar predicción';
      setErrorModal({
        open: true,
        title: 'Error al guardar',
        message: errorMessage
      });
    } finally {
      setSavingMatch(null);
    }
  };

  const handleEdit = (matchId) => {
    setEditingMatch(matchId);
  };

  const handleCancelEdit = (matchId) => {
    // Restaurar predicción guardada
    const match = matches.find(m => m.id === matchId);
    if (match?.prediction) {
      setPredictions(prev => ({
        ...prev,
        [matchId]: { home_goals: match.prediction.home_goals, away_goals: match.prediction.away_goals }
      }));
    }
    setEditingMatch(null);
  };

  // Filtrar partidos
  let filtered = matches;
  
  // Filtro por etapa
  if (selectedStage !== 'all') {
    filtered = filtered.filter(m => m.stage?.id === selectedStage);
  }
  
  // Filtro por tipo (pendientes/historial)
  if (filterType === 'pendientes') {
    filtered = filtered.filter(m => m.status !== 'finalizado');
  } else {
    filtered = filtered.filter(m => m.status === 'finalizado');
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="predictions-page">
      <h2 className="text-2xl lg:text-3xl font-extrabold gold-text" style={{ fontFamily: 'Montserrat' }}>
        {filterType === 'pendientes' ? 'Predicciones de Partidos' : 'Historial de Predicciones'}
      </h2>
      <p className="text-white/50 text-sm">
        {filterType === 'pendientes' ? 'Predice el resultado final:' : 'Revisa tus predicciones y puntos ganados:'}
      </p>

      {/* Filtros */}
      <div className="flex flex-col lg:flex-row gap-3">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="bg-[#111A30] border-[#F4E39E]/30 text-white lg:max-w-xs" data-testid="filter-type">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
            <SelectItem value="pendientes" className="text-white">Pendientes</SelectItem>
            <SelectItem value="historial" className="text-white">Historial</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedStage} onValueChange={setSelectedStage}>
          <SelectTrigger className="bg-[#111A30] border-[#F4E39E]/30 text-white lg:max-w-xs" data-testid="prediction-stage-filter">
            <SelectValue placeholder="Selecciona la Etapa" />
          </SelectTrigger>
          <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
            <SelectItem value="all" className="text-white">Todas las etapas</SelectItem>
            {stages.map(s => (
              <SelectItem key={s.id} value={s.id} className="text-white">{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center text-white/50 py-10">Cargando...</div>
      ) : filtered.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">
          {filterType === 'pendientes' ? 'No hay partidos pendientes para predecir' : 'No hay partidos finalizados aún'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((match) => {
            const isLocked = match.is_locked;
            const isSaved = savedPredictions[match.id];
            const isEditing = editingMatch === match.id;
            const isFinalizado = match.status === 'finalizado';
            const result = results[match.id];
            const points = isFinalizado && match.prediction ? calculatePoints(match.prediction, result) : 0;
            
            // Para partidos pendientes: mostrar como caja cerrada si está guardado y no está en modo edición
            const showClosed = filterType === 'pendientes' && isSaved && !isEditing && !isLocked;
            
            return (
              <div 
                key={match.id} 
                className={`glass-card p-4 ${isLocked ? 'opacity-60' : ''}`} 
                data-testid={`prediction-card-${match.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#F4E39E] font-semibold">{match.stage?.name}</span>
                  <div className="flex items-center gap-2">
                    {isFinalizado && filterType === 'historial' && (
                      <Badge className="bg-[#F4E39E]/20 text-[#F4E39E] text-xs font-bold">
                        {points} pts
                      </Badge>
                    )}
                    {isLocked && (
                      <Badge className="bg-red-500/20 text-red-400 text-[10px] flex items-center gap-1">
                        <Lock size={10} /> CERRADO
                      </Badge>
                    )}
                  </div>
                </div>
                
                {/* Modo caja cerrada con botón editar */}
                {showClosed ? (
                  <div className="bg-[#0A0F1C]/50 border border-[#F4E39E]/20 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <img src={FLAG_URL(match.home_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                        <span className="text-white font-bold text-sm">{match.home_team?.name}</span>
                      </div>
                      <span className="text-white font-black text-xl">{predictions[match.id]?.home_goals ?? 0}</span>
                    </div>
                    <div className="text-center gold-text font-black text-sm mb-3" style={{ fontFamily: 'Montserrat' }}>VS</div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <img src={FLAG_URL(match.away_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                        <span className="text-white font-bold text-sm">{match.away_team?.name}</span>
                      </div>
                      <span className="text-white font-black text-xl">{predictions[match.id]?.away_goals ?? 0}</span>
                    </div>
                    <div className="text-white/40 text-xs text-center mb-3">
                      Fecha: {match.date} | Hora: {match.time}
                    </div>
                    <button
                      onClick={() => handleEdit(match.id)}
                      className="w-full bg-[#F4E39E]/20 hover:bg-[#F4E39E]/30 border border-[#F4E39E]/40 text-[#F4E39E] font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Edit2 size={16} />
                      Editar Predicción
                    </button>
                  </div>
                ) : (
                  /* Modo edición normal */
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1">
                        {match.favorite_home && (
                          <Star size={20} className="text-[#FFD700] fill-[#FFD700]" style={{ filter: 'drop-shadow(0 0 8px #FFD700)', strokeWidth: 3, flexShrink: 0 }} title="Favorito" />
                        )}
                        <img src={FLAG_URL(match.home_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                        <span className="text-white font-bold text-sm">{match.home_team?.name}</span>
                      </div>
                      {filterType === 'historial' && isFinalizado ? (
                        <div className="flex items-center gap-2">
                          <span className="text-white/50 text-sm">Pred: {match.prediction?.home_goals ?? '-'}</span>
                          <span className="text-white font-black text-xl">{result?.home_goals ?? '-'}</span>
                        </div>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          max="20"
                          value={predictions[match.id]?.home_goals ?? ''}
                          onChange={(e) => updatePrediction(match.id, 'home_goals', e.target.value)}
                          placeholder="0"
                          disabled={isLocked || (isSaved && !isEditing)}
                          data-testid={`prediction-home-${match.id}`}
                          className="w-14 h-12 bg-[#0A0F1C] border border-[#F4E39E]/30 rounded-lg text-white font-bold text-center text-xl focus:border-[#F4E39E] focus:ring-1 focus:ring-[#F4E39E] outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      )}
                    </div>
                    <div className="text-center gold-text font-black text-sm mb-3" style={{ fontFamily: 'Montserrat' }}>VS</div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1">
                        {match.favorite_away && (
                          <Star size={20} className="text-[#FFD700] fill-[#FFD700]" style={{ filter: 'drop-shadow(0 0 8px #FFD700)', strokeWidth: 3, flexShrink: 0 }} title="Favorito" />
                        )}
                        <img src={FLAG_URL(match.away_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                        <span className="text-white font-bold text-sm">{match.away_team?.name}</span>
                      </div>
                      {filterType === 'historial' && isFinalizado ? (
                        <div className="flex items-center gap-2">
                          <span className="text-white/50 text-sm">Pred: {match.prediction?.away_goals ?? '-'}</span>
                          <span className="text-white font-black text-xl">{result?.away_goals ?? '-'}</span>
                        </div>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          max="20"
                          value={predictions[match.id]?.away_goals ?? ''}
                          onChange={(e) => updatePrediction(match.id, 'away_goals', e.target.value)}
                          placeholder="0"
                          disabled={isLocked || (isSaved && !isEditing)}
                          data-testid={`prediction-away-${match.id}`}
                          className="w-14 h-12 bg-[#0A0F1C] border border-[#F4E39E]/30 rounded-lg text-white font-bold text-center text-xl focus:border-[#F4E39E] focus:ring-1 focus:ring-[#F4E39E] outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      )}
                    </div>
                    <div className="text-white/40 text-xs text-center mb-3">
                      Fecha: {match.date} | Hora: {match.time}
                      {match.venue?.city && <span> | Sede: {match.venue.city}, {match.stadium?.name}</span>}
                      {isLocked && <div className="text-red-400 mt-1 font-semibold">⏱ Partido iniciado - No se pueden modificar predicciones</div>}
                    </div>
                    
                    {/* Botones de acción */}
                    {filterType === 'pendientes' && !isLocked && (
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => handleSaveIndividual(match.id)}
                              disabled={savingMatch === match.id}
                              className="flex-1 bg-green-500/20 hover:bg-green-500/30 border border-green-500/40 text-green-400 font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                              <Check size={16} />
                              {savingMatch === match.id ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button
                              onClick={() => handleCancelEdit(match.id)}
                              disabled={savingMatch === match.id}
                              className="bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 font-bold py-2 px-4 rounded-lg transition-colors"
                            >
                              <X size={16} />
                            </button>
                          </>
                        ) : !isSaved && (
                          <button
                            onClick={() => handleSaveIndividual(match.id)}
                            disabled={savingMatch === match.id}
                            className="w-full gold-btn py-2 px-4 rounded-lg font-bold"
                          >
                            {savingMatch === match.id ? 'Guardando...' : 'Guardar Predicción'}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {filterType === 'pendientes' && filtered.length > 0 && (
        <p className="text-white/30 text-xs text-center">Guarda tus predicciones antes de la hora de inicio de cada partido.</p>
      )}

      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
